import React, { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MainPageBase } from "../page-bases/MainPageBase";
import DashboardMetricCard from "../../components/Analytics/DashboardMetricCard";
import DashboardFilters from "../../components/Analytics/DashboardFilters";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import {
  refreshDashboardData,
  setTimePeriod,
  setLocation,
  setCategory,
  setAutoRefresh,
  selectDashboardMetrics,
  selectDashboardFilters,
  selectDashboardLoading,
  selectDashboardError,
  selectDashboardLastUpdated,
  selectAvailableLocations,
  selectAvailableCategories,
} from "../../store/reducers/analytics/dashboard.reducer";
import { AssetCountMetricsController } from "../../components/Analytics/assets/AssetCountMetricsController";
import { GeneralMetricsUI } from "../../components/Analytics/GeneralMetricsUI";
import { MaintenanceSchedulesMetricsController } from "../../components/Analytics/MaintenanceSchedulesMetricsController";

import { DashboardAlertController } from "../../components/Analytics/DashboardAlertController";
import { ProgrammesMetricsController } from "../../components/Analytics/domain/ProgrammesMetricsController";
import { FacilitiesMetricsController } from "../../components/Analytics/domain/FacilitiesMetricsController";
import { DistrictsMetricsController } from "../../components/Analytics/domain/DistrictsMetricsController";
import { UserMetricsController } from "../../components/Analytics/user/UserMetricsController";

function DashboardMainPage() {
  const dispatch = useDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Selectors
  const metrics = useSelector(selectDashboardMetrics);

  const filters = useSelector(selectDashboardFilters);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const lastUpdated = useSelector(selectDashboardLastUpdated);
  const availableLocations = useSelector(selectAvailableLocations);
  const availableCategories = useSelector(selectAvailableCategories);

  // Initialize dashboard data
  useEffect(() => {
    dispatch(refreshDashboardData(filters.timePeriod) as any);
  }, [dispatch, filters.timePeriod]);

  // Auto-refresh functionality
  useEffect(() => {
    if (filters.autoRefresh) {
      intervalRef.current = setInterval(() => {
        dispatch(refreshDashboardData(filters.timePeriod) as any);
      }, 30000); // Refresh every 30 seconds
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [filters.autoRefresh, filters.timePeriod, dispatch]);

  // Handlers
  const handleTimePeriodChange = useCallback(
    (period: string) => {
      dispatch(setTimePeriod(period));
    },
    [dispatch]
  );

  const handleLocationChange = useCallback(
    (location: string) => {
      dispatch(setLocation(location || undefined));
    },
    [dispatch]
  );

  const handleCategoryChange = useCallback(
    (category: string) => {
      dispatch(setCategory(category || undefined));
    },
    [dispatch]
  );

  const handleAutoRefreshChange = useCallback(
    (enabled: boolean) => {
      dispatch(setAutoRefresh(enabled));
    },
    [dispatch]
  );

  const handleRefresh = useCallback(() => {
    dispatch(refreshDashboardData(filters.timePeriod) as any);
  }, [dispatch, filters.timePeriod]);

  const handleExport = useCallback(() => {
    // Create CSV data from dashboard metrics and chart data
    const csvData = [
      ["Metric", "Total", "Active", "Inactive"],
      [
        "Assets",
        metrics?.totalAssets.total ?? 0,
        metrics?.totalAssets.active ?? 0,
        metrics?.totalAssets.inactive ?? 0,
      ],
      [
        "Users",
        metrics?.totalUsers.total ?? 0,
        metrics?.totalUsers.active ?? 0,
        metrics?.totalUsers.inactive ?? 0,
      ],
      [
        "Maintenance",
        metrics?.totalMaintenance.total ?? 0,
        metrics?.totalMaintenance.active ?? 0,
        metrics?.totalMaintenance.inactive ?? 0,
      ],
      [
        "Dispatch",
        metrics?.totalDispatch.total ?? 0,
        metrics?.totalDispatch.active ?? 0,
        metrics?.totalDispatch.inactive ?? 0,
      ],
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `dashboard-metrics-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [metrics]);

  const getMetricCardIcon = (type: string) => {
    switch (type) {
      case "assets":
        return "🏭";
      case "users":
        return "👥";
      case "maintenance":
        return "🔧";
      case "dispatch":
        return "🚚";
      case "efficiency":
        return "⚡";
      case "compliance":
        return "✅";
      case "distributions":
        return "📦";
      case "pending":
        return "⏳";
      case "transit":
        return "🚛";
      case "received":
        return "✅";
      case "rejected":
        return "❌";
      case "acceptance":
        return "🎯";
      case "rejection":
        return "📉";
      case "flow":
        return "📈";
      // Donor & Batch Analytics icons
      case "donors":
        return "🤝";
      case "batches":
        return "📦";
      case "average":
        return "📊";
      // Asset Lifecycle icons
      case "age":
        return "⏰";
      case "warranty":
        return "🛡️";
      case "expired":
        return "⚠️";
      case "compliance-warranty":
        return "📋";
      default:
        return "📊";
    }
  };

  if (error) {
    return (
      <MainPageBase
        title="Dashboard"
        subtitle="KPI and Key Metrics"
        pageState="main"
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Error Loading Dashboard
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainPageBase>
    );
  }

  return (
    <MainPageBase
      title="Dashboard"
      subtitle="KPI and Key Metrics"
      pageState="main"
    >
      <div className="space-y-6 p-6">
        <GeneralMetricsUI />
        <ExpandableContainer label="Assets Analytics" initOpenState={true}>
          <AssetCountMetricsController />
        </ExpandableContainer>

        <ExpandableContainer
          label="Maintenance Schedules Analytics"
          initOpenState={true}
        >
          <MaintenanceSchedulesMetricsController />
        </ExpandableContainer>
        <ExpandableContainer label="System Alerts" initOpenState={true}>
          <DashboardAlertController />
        </ExpandableContainer>

        <ExpandableContainer label="Users Analytics" initOpenState={true}>
          <UserMetricsController />
        </ExpandableContainer>

        <ExpandableContainer
          label="Domain Programmes Analytics"
          initOpenState={true}
        >
          <ProgrammesMetricsController />
        </ExpandableContainer>

        <ExpandableContainer
          label="Domain Facilities Analytics"
          initOpenState={true}
        >
          <FacilitiesMetricsController />
        </ExpandableContainer>

        <ExpandableContainer
          label="Domain Districts Analytics"
          initOpenState={true}
        >
          <DistrictsMetricsController />
        </ExpandableContainer>

        {/* Filters */}
        <DashboardFilters
          selectedTimePeriod={filters.timePeriod}
          onTimePeriodChange={handleTimePeriodChange}
          selectedLocation={filters.location}
          onLocationChange={handleLocationChange}
          selectedCategory={filters.category}
          onCategoryChange={handleCategoryChange}
          autoRefresh={filters.autoRefresh}
          onAutoRefreshChange={handleAutoRefreshChange}
          onRefresh={handleRefresh}
          onExport={handleExport}
          loading={loading}
          locations={availableLocations}
          categories={availableCategories}
        />

        {/* Donor & Batch Analytics */}
        <ExpandableContainer
          label="Donor & Batch Analytics"
          initOpenState={true}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            <DashboardMetricCard
              title="Total Donors"
              value={metrics?.totalDonors ?? 0}
              subtitle="Contributing organizations"
              icon={getMetricCardIcon("donors")}
              color="#8B5CF6"
              loading={loading}
            />

            <DashboardMetricCard
              title="Active Donors"
              value={metrics?.activeDonors ?? 0}
              subtitle="Currently contributing"
              icon={getMetricCardIcon("donors")}
              color="#10B981"
              loading={loading}
            />

            <DashboardMetricCard
              title="Total Batches"
              value={metrics?.totalBatches ?? 0}
              subtitle="Asset batches received"
              icon={getMetricCardIcon("batches")}
              color="#3B82F6"
              loading={loading}
            />

            <DashboardMetricCard
              title="Average Assets per Batch"
              value={metrics?.averageAssetsPerBatch ?? 0}
              subtitle="Assets per donation batch"
              icon={getMetricCardIcon("average")}
              color="#F59E0B"
              loading={loading}
            />
          </div>
        </ExpandableContainer>

        {/* Last Updated */}
        {lastUpdated && (
          <div className="text-center text-sm text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </div>
        )}
      </div>
    </MainPageBase>
  );
}

export default DashboardMainPage;
